import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { lastValueFrom } from 'rxjs';
import { OlimpiaService } from '../../services/olimpia.service';
import { BecomeService } from '../../services/become.service';
import { IdentityProviderCode, IdentityValidationProviderService } from '../../services/identity-validation-provider.service';
import { WhatsappSessionService } from '../../services/whatsapp-session.service';
import { ErrorScreenComponent } from 'app/shared/error-screen/error-screen.component';
import { WowComponent } from 'app/shared/wow/wow.component';
import { LoaderService } from 'app/services/loader.service';
import { ModalComponent } from 'app/shared/modal/modal.component';
import { SecurityService } from '../../services/security.service';
import { environment } from '@env/environment';

type PlatformKind = 'ios' | 'android' | 'other';

@Component({
  selector: 'app-whatsapp-validation',
  standalone: true,
  imports: [
    CommonModule,
    ErrorScreenComponent,
    WowComponent,
    ModalComponent,
  ],
  templateUrl: './whatsapp-validation.component.html',
  styleUrl: './whatsapp-validation.component.css'
})
export class WhatsappValidationComponent implements OnInit, OnDestroy {
  hash = '';
  leaving = false;
  externalRedirectUrl = '';
  processGuid = '';
  documentTypeId: number | null = null;
  validationOlimpiaUrl: SafeResourceUrl | null = null;
  titleError = '';
  descriptionError = '';
  errorActionLabel = 'Reintentar';
  identityErrorKind: 'retry' | 'fraud' | 'correct' = 'retry';
  showErrorAdvisor = true;

  showOlimpia = signal(false);
  wowDisplay = signal(false);
  showError = signal(false);
  showFaceGuide = signal(false);
  showWhatsappWarning = signal(false);
  showManualFallback = signal(false);
  showCompleted = signal(false);
  countdown = signal(5);

  becomeUserId = '';
  becomeSession: {
    accessToken: string;
    userId: string;
    contractId: string;
    country: string;
    docType: string;
  } | null = null;

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pollStartTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private countdownId: ReturnType<typeof setInterval> | null = null;
  private becomeButtonEl: HTMLElement | null = null;
  private becomeSdkLoading: Promise<void> | null = null;
  private becomeFrameObserver: MutationObserver | null = null;
  private becomeAutoClickTimer: ReturnType<typeof setTimeout> | null = null;
  private becomeFrameOpened = false;
  private becomeCycleFinished = false;
  private providerFallbackUsed = false;

  constructor(
    private route: ActivatedRoute,
    private olimpiaService: OlimpiaService,
    private becomeService: BecomeService,
    private identityValidationProviderService: IdentityValidationProviderService,
    private whatsappSessionService: WhatsappSessionService,
    private sanitizer: DomSanitizer,
    private loaderService: LoaderService,
    private securityService: SecurityService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.hash = params.get('hash') ?? '';
      if (!this.hash) {
        this.failMessage('Hash no encontrado', 'Error');
        return;
      }

      if (this.isValidationConsumed()) {
        this.showAlreadyCompleted();
        return;
      }

      this.externalRedirectUrl = this.buildAppRedirectUrl();
      this.bootstrapFlow();
    });
  }

  ngOnDestroy(): void {
    this.clearTimers();
    this.cleanupBecomeButton();
    this.stopBecomeFrameObserver();
  }

  getExternalRedirectUrl(): string {
    return this.buildAppRedirectUrl();
  }

  proceedWithValidation(): Promise<void> {
    if (this.isInAppBrowser()) {
      this.showFaceGuide.set(false);
      this.showOlimpia.set(false);
      this.validationOlimpiaUrl = null;
      this.showWhatsappWarning.set(true);
      return Promise.resolve();
    }

    this.setFlag('face_guide_shown', true);
    this.showFaceGuide.set(false);
    return this.startIdentityValidation();
  }

  openInExternalBrowserAndContinue(): void {
    this.openAppInExternalBrowser();
  }

  onIosExternalLinkClick(): void {
    this.showOlimpia.set(false);
    this.validationOlimpiaUrl = null;
    this.clearPollTimers();

    this.externalRedirectUrl = this.buildAppRedirectUrl();
    this.prepareExternalRedirect();
    this.armManualFallback();
  }

  copyLinkToClipboard(): void {
    const url = this.buildAppRedirectUrl();
    this.externalRedirectUrl = url;
    navigator.clipboard.writeText(url).then(() => {
      alert('Enlace copiado. Ábrelo en Safari desde el portapapeles.');
    }).catch((err) => {
      console.error('No se pudo copiar el enlace:', err);
    });
  }

  closeModal(): void {
    this.showOlimpia.set(false);
    this.clearPollTimers();
  }

  failMessage(description: string, title: string = 'Error', kind: 'retry' | 'fraud' | 'correct' = 'retry'): void {
    this.titleError = title;
    this.descriptionError = description;
    this.identityErrorKind = kind;
    this.showErrorAdvisor = kind !== 'fraud';
    this.errorActionLabel = kind === 'fraud' ? 'Cerrar' : kind === 'correct' ? 'Volver al chat' : 'Reintentar';
    this.showError.set(true);
    this.loaderService.hide();
  }

  closeIdentityError(): void {
    if (this.identityErrorKind === 'fraud') {
      window.location.replace('https://www.fincomercio.com/');
      return;
    }
    if (this.identityErrorKind === 'correct') {
      this.showError.set(false);
      this.returnToChat('Debo corregir un dato de mi vinculación antes de validar la identidad.');
      return;
    }
    this.retry();
  }

  showWow(): void {
    this.wowDisplay.set(true);
    this.loaderService.hide();
    this.markValidationConsumed();
  }

  next(): void {
    if (this.leaving) return;
    this.leaving = true;
    this.markValidationConsumed();
    this.clearWhatsappValidationData();
    const phoneNumber = `57${environment.whatsappNumber}`;
    const message = encodeURIComponent('Ya validé mi identidad.');
    window.location.replace(`https://wa.me/${phoneNumber}?text=${message}`);
    setTimeout(() => {
      try {
        window.close();
      } catch {
        // El navegador puede bloquear el cierre de la pestaña.
      }
      if (!document.hidden) {
        this.redirectAway();
      }
    }, 1200);
  }

  private returnToChat(text: string): void {
    if (this.leaving) return;
    this.leaving = true;
    const phoneNumber = `57${environment.whatsappNumber}`;
    window.location.replace(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`);
  }

  retry(): void {
    this.showError.set(false);
    this.titleError = '';
    this.descriptionError = '';
    void this.startIdentityValidation();
  }

  isAndroidUA(): boolean {
    return this.platform() === 'android';
  }

  private openAppInExternalBrowser(): void {
    this.showOlimpia.set(false);
    this.validationOlimpiaUrl = null;
    this.clearPollTimers();

    const appUrl = this.buildAppRedirectUrl();
    this.externalRedirectUrl = appUrl;
    this.prepareExternalRedirect();
    this.openExternalBrowser(appUrl);
    this.armManualFallback();
  }

  private buildAppRedirectUrl(): string {
    const origin = window.location.origin;
    return `${origin}/app/whatsapp/${this.hash}?from_redirect=true`;
  }

  private bootstrapFlow(): void {
    if (this.isInAppBrowser()) {
      this.blockOlimpiaInsideWhatsapp();
      return;
    }

    this.verifyWhatsappSession();
  }

  private blockOlimpiaInsideWhatsapp(): void {
    this.showWhatsappWarning.set(true);
    this.showFaceGuide.set(false);
    this.showOlimpia.set(false);
    this.validationOlimpiaUrl = null;
    this.clearPollTimers();
    this.loaderService.hide();
  }

  private async verifyWhatsappSession(): Promise<void> {
    const deviceData = await this.securityService.collectDevicePayload();

    this.whatsappSessionService.verifySessionWhatsapp(this.hash, deviceData).subscribe({
      next: (response: any) => {
        if (response?.alreadyCompleted) {
          this.showAlreadyCompleted();
          return;
        }

        if (this.isInAppBrowser()) {
          this.blockOlimpiaInsideWhatsapp();
          return;
        }

        localStorage.setItem('session', response.session);
        const documentTypeId = Number(response.documentTypeId);
        this.documentTypeId = Number.isFinite(documentTypeId) && documentTypeId > 0
          ? documentTypeId
          : null;
        this.showFaceGuide.set(true);
      },
      error: (error) => {
        console.error(error);
        this.failMessage('Sesión inválida o expirada', 'Sesión inválida');
      }
    });
  }

  private async startIdentityValidation(): Promise<void> {
    if (this.isInAppBrowser()) {
      this.blockOlimpiaInsideWhatsapp();
      return;
    }

    this.loaderService.show('Preparando validación de identidad...');
    let provider: IdentityProviderCode = 'OLIMPIA';
    try {
      provider = await lastValueFrom(this.identityValidationProviderService.resolveForOnboarding());
    } catch (error) {
      console.error('Error resolving identity provider', error);
      provider = 'OLIMPIA';
    }
    this.loaderService.hide();
    this.providerFallbackUsed = false;
    await this.startProvider(provider);
  }

  private async startProvider(provider: IdentityProviderCode): Promise<void> {
    if (provider === 'BECOME') {
      await this.startBecome();
      return;
    }
    this.startOlimpia();
  }

  /** Solo para fallas técnicas al iniciar; un rechazo de identidad no cambia de proveedor. */
  private handleProviderStartFailure(failed: IdentityProviderCode, error?: unknown): void {
    console.error(`Fallo al iniciar ${failed}`, error);
    this.loaderService.hide();
    const alternative: IdentityProviderCode = failed === 'OLIMPIA' ? 'BECOME' : 'OLIMPIA';

    if (!this.providerFallbackUsed) {
      this.providerFallbackUsed = true;
      this.startProvider(alternative);
      return;
    }

    this.failMessage('No fue posible iniciar la validación de identidad. Intente de nuevo más tarde o comuníquese con su asesor.');
  }

  private startOlimpia(): void {
    if (this.isInAppBrowser()) {
      this.blockOlimpiaInsideWhatsapp();
      return;
    }

    this.loaderService.show('Iniciando validación...');

    this.olimpiaService.getWhatsappValidation().subscribe({
      next: (response: any) => {
        if (this.isInAppBrowser()) {
          this.blockOlimpiaInsideWhatsapp();
          return;
        }

        if (response?.alreadyCompleted) {
          this.showAlreadyCompleted();
          return;
        }

        const data = response.data;
        if (!data?.url || !data?.procesoConvenioGuid) {
          this.handleProviderStartFailure('OLIMPIA', response);
          return;
        }

        this.processGuid = data.procesoConvenioGuid;
        this.validationOlimpiaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.url);
        this.loaderService.hide();
        this.showOlimpia.set(true);
        this.startInterval();
      },
      error: (error) => {
        this.handleProviderStartFailure('OLIMPIA', error);
      }
    });
  }

  private async startBecome(): Promise<void> {
    if (this.isInAppBrowser()) {
      this.blockOlimpiaInsideWhatsapp();
      return;
    }

    this.loaderService.show('Abriendo validación Become, por favor espere...');
    try {
      const response: any = await lastValueFrom(
        this.becomeService.startSession(this.documentTypeId ?? 1)
      );
      if (response?.data?.reused) {
        this.applyBecomeResult({ finalizado: true, aprobado: true });
        return;
      }
      await this.loadBecomeSdk();
      const data = response?.data;
      if (!data?.userId || !data?.accessToken) {
        this.handleProviderStartFailure('BECOME', response);
        return;
      }
      this.becomeSession = data;
      this.becomeUserId = data.userId;
      this.mountBecomeButton();
    } catch (error: any) {
      if (error?.status === 403 && error?.error?.motivo === 'FRAUDE') {
        this.applyBecomeResult({ motivo: 'FRAUDE', finalizado: true, aprobado: false });
        return;
      }
      this.handleProviderStartFailure('BECOME', error);
    }
  }

  private loadBecomeSdk(): Promise<void> {
    if (customElements.get('become-button')) {
      return Promise.resolve();
    }
    if (this.becomeSdkLoading) {
      return this.becomeSdkLoading;
    }

    this.becomeSdkLoading = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[data-become-sdk="v2"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Become SDK load error')));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://onboarding-v2.svi.becomedigital.net/resources/button.js';
      script.async = true;
      script.dataset['becomeSdk'] = 'v2';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Become SDK load error'));
      document.head.appendChild(script);
    });

    return this.becomeSdkLoading;
  }

  private mountBecomeButton(): void {
    if (!this.becomeSession) {
      return;
    }

    this.becomeFrameOpened = false;
    this.becomeCycleFinished = false;
    this.cleanupBecomeButton();
    this.loaderService.show('Abriendo validación Become, por favor espere...');

    const button = document.createElement('become-button');
    const session = this.becomeSession;
    const host = button as HTMLElement & {
      userId?: string;
      token?: string;
      contractId?: string;
      country?: string;
      docType?: string;
    };
    host.userId = session.userId;
    host.token = session.accessToken;
    host.contractId = String(session.contractId);
    host.country = session.country || 'CO';
    host.docType = session.docType || 'national-id';
    button.setAttribute('userId', session.userId);
    button.setAttribute('contractId', String(session.contractId));
    button.setAttribute('token', session.accessToken);
    button.setAttribute('country', session.country || 'CO');
    button.setAttribute('docType', session.docType || 'national-id');
    button.style.display = 'block';
    button.style.margin = '0 auto';
    button.style.width = '240px';
    button.style.height = '72px';
    button.style.opacity = '0';
    button.style.overflow = 'hidden';

    button.addEventListener('become:userFinishedSdk', ((event: CustomEvent) => {
      const detail = event.detail || {};
      this.becomeUserId = detail.userId || this.becomeUserId;
      this.becomeCycleFinished = true;
      this.clearBecomeAutoOpenTimers();
      this.showFaceGuide.set(false);
      this.loaderService.show('Validando identidad Become, por favor espere...');
      this.startBecomeInterval();
    }) as EventListener);

    button.addEventListener('become:exitedSdk', (() => {
      if (this.becomeCycleFinished || !this.becomeFrameOpened) {
        return;
      }
      this.clearBecomeAutoOpenTimers();
      this.hideBecome();
      this.loaderService.hide();
      this.failMessage('Validación Become cancelada. Puede intentarlo de nuevo.');
    }) as EventListener);

    const container = document.getElementById('become-sdk-container');
    if (container) {
      container.appendChild(button);
    } else {
      document.body.appendChild(button);
    }
    this.becomeButtonEl = button;
    this.startBecomeFrameObserver(() => {
      this.becomeFrameOpened = true;
      this.showFaceGuide.set(false);
      this.clearBecomeAutoOpenTimers();
      this.loaderService.hide();
    });

    let attempts = 0;
    let clicked = false;
    const tryClick = () => {
      if (clicked || this.becomeFrameOpened || document.querySelector('become-frame')) {
        return;
      }
      const clickHost = button as HTMLElement & { userId?: string; token?: string };
      if (clickHost.userId && clickHost.token) {
        clicked = true;
        clickHost.click();
        return;
      }
      attempts++;
      if (attempts < 25) {
        this.becomeAutoClickTimer = setTimeout(tryClick, 200);
        return;
      }
      this.loaderService.hide();
    };
    this.becomeAutoClickTimer = setTimeout(tryClick, 200);
  }

  private hideBecome(): void {
    this.becomeCycleFinished = true;
    this.clearBecomeAutoOpenTimers();
    this.cleanupBecomeButton();
    this.stopBecomeFrameObserver();
    this.clearPollTimers();
  }

  private startBecomeInterval(): void {
    this.clearPollTimers();
    this.pollStartTimeoutId = setTimeout(() => {
      this.intervalId = setInterval(() => {
        this.becomeService.verifyValidate(this.becomeUserId).subscribe({
          next: (response: any) => {
            const data = response.data;
            if (!data?.finalizado && !data?.cancelado) {
              return;
            }
            this.applyBecomeResult(data);
          },
          error: (error) => {
            if (error?.status === 403 && error?.error?.motivo === 'FRAUDE') {
              this.applyBecomeResult({ motivo: 'FRAUDE', finalizado: true, aprobado: false });
              return;
            }
            console.error(error);
          }
        });
      }, 10000);
    }, 15000);
  }

  private applyBecomeResult(data: any): void {
    this.hideBecome();
    this.loaderService.hide();
    if (data?.motivo === 'FRAUDE') {
      this.failMessage(
        'No es posible continuar con su solicitud.',
        'Solicitud no disponible',
        'fraud',
      );
      return;
    }
    if (data?.motivo === 'CORREGIR_DATOS') {
      this.failMessage(
        'El número de cédula no coincide. Vuelva al chat, corrija el número y vuelva a validar su identidad.',
        'Corrija la cédula',
        'correct',
      );
      return;
    }
    if (data?.aprobado) {
      this.showWow();
      return;
    }
    this.failMessage(
      'La validación de identidad no fue exitosa. Repita la captura del documento y del rostro.',
      'Repita la validación',
    );
  }

  private clearBecomeAutoOpenTimers(): void {
    if (this.becomeAutoClickTimer) {
      clearTimeout(this.becomeAutoClickTimer);
      this.becomeAutoClickTimer = null;
    }
  }

  private startBecomeFrameObserver(onOpened?: () => void): void {
    this.stopBecomeFrameObserver();

    const patchIframes = (root: ParentNode | ShadowRoot) => {
      root.querySelectorAll('iframe').forEach((iframe) => {
        const currentAllow = iframe.getAttribute('allow') || '';
        const required = ['camera', 'microphone', 'fullscreen', 'autoplay'];
        const missing = required.filter((item) => !currentAllow.includes(item));
        if (missing.length > 0 || !currentAllow) {
          iframe.setAttribute(
            'allow',
            'camera; microphone; fullscreen; autoplay; clipboard-write'
          );
        }
        iframe.setAttribute('allowfullscreen', 'true');
      });
    };

    const scan = () => {
      const frames = document.querySelectorAll('become-frame');
      if (frames.length > 0 && onOpened) {
        onOpened();
        onOpened = undefined;
      }
      frames.forEach((frame) => {
        patchIframes(document);
        if (frame.shadowRoot) {
          patchIframes(frame.shadowRoot);
        }
      });
      patchIframes(document);
    };

    this.becomeFrameObserver = new MutationObserver(() => scan());
    this.becomeFrameObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['allow', 'src'],
    });
    scan();
  }

  private stopBecomeFrameObserver(): void {
    if (this.becomeFrameObserver) {
      this.becomeFrameObserver.disconnect();
      this.becomeFrameObserver = null;
    }
  }

  private cleanupBecomeButton(): void {
    if (this.becomeButtonEl?.parentElement) {
      this.becomeButtonEl.parentElement.removeChild(this.becomeButtonEl);
    }
    this.becomeButtonEl = null;
    const container = document.getElementById('become-sdk-container');
    if (container) {
      container.innerHTML = '';
    }
    document.querySelectorAll('become-frame').forEach((frame) => frame.remove());
  }

  private prepareExternalRedirect(): void {
    this.setFlag('verified', true);
    this.setFlag('hash', this.hash);
    sessionStorage.setItem(this.storageKey('from_redirect'), 'true');
    this.showWhatsappWarning.set(false);
  }

  private openExternalBrowser(url: string): void {
    if (!url.includes('/app/whatsapp/')) {
      url = this.buildAppRedirectUrl();
    }

    try {
      if (this.platform() === 'android') {
        this.loaderService.show('Abriendo navegador externo...');
        const withoutScheme = url.replace(/^https?:\/\//, '');
        const intentUrl =
          `intent://${withoutScheme}` +
          `#Intent;scheme=https;package=com.android.chrome;` +
          `S.browser_fallback_url=${encodeURIComponent(url)};end`;

        window.location.href = intentUrl;
        setTimeout(() => {
          this.openUrlWithAnchor(url);
          this.loaderService.hide();
        }, 700);
        return;
      }

      const popup = window.open(url, '_blank', 'noopener,noreferrer');
      if (!popup) {
        this.openUrlWithAnchor(url);
        setTimeout(() => {
          if (this.isInAppBrowser() || window.location.href.includes('olimpiait.com')) {
            window.location.assign(url);
          }
        }, 300);
      }
    } catch (error) {
      console.error('Error abriendo navegador externo:', error);
      window.location.assign(url);
      this.loaderService.hide();
    }
  }

  private openUrlWithAnchor(url: string): void {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private armManualFallback(): void {
    if (this.fallbackTimeoutId) {
      clearTimeout(this.fallbackTimeoutId);
    }
    this.fallbackTimeoutId = setTimeout(() => {
      this.showManualFallback.set(true);
      this.showWhatsappWarning.set(true);
      this.loaderService.hide();
    }, 2500);
  }

  private startInterval(): void {
    this.clearPollTimers();

    this.pollStartTimeoutId = setTimeout(() => {
      this.intervalId = setInterval(() => {
        this.olimpiaService.verifyWhatsappValidation(this.processGuid).subscribe({
          next: (response: any) => {
            const data = response.data;
            if (!data.finalizado && !data.cancelado) {
              return;
            }

            this.closeModal();
            if (data.aprobado) {
              this.showWow();
            } else {
              this.failMessage(
                '106-E: Ha fallado la validación del usuario, intente de nuevo, acérquese a una oficina o comuníquese con su asesor...'
              );
            }
          },
          error: (error) => console.error(error)
        });
      }, 5000);
    }, 8000);
  }

  private isInAppBrowser(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    const ua = userAgent.toLowerCase();

    if (
      ua.includes('whatsapp') ||
      ua.includes('fbav') ||
      ua.includes('fban') ||
      ua.includes('instagram') ||
      ua.includes('line/') ||
      ua.includes('tiktok')
    ) {
      return true;
    }

    if (this.platform() === 'ios') {
      const isRealSafari =
        /Safari\//i.test(userAgent) &&
        /Version\//i.test(userAgent) &&
        !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(userAgent);

      if (!isRealSafari) {
        return true;
      }
    }

    if (this.platform() === 'android') {
      if (/; wv\)/i.test(userAgent) || /\bwv\b/i.test(userAgent)) {
        return true;
      }
      if (!/Chrome\/\d+/i.test(userAgent) && !/Firefox\/\d+/i.test(userAgent)) {
        return true;
      }
    }

    return false;
  }

  private platform(): PlatformKind {
    const userAgent = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'ios';
    }
    if (/Android/i.test(userAgent)) {
      return 'android';
    }
    return 'other';
  }

  private storageKey(suffix: string): string {
    return `whatsapp_validation_${suffix}_${this.hash}`;
  }

  private isValidationConsumed(): boolean {
    return localStorage.getItem(this.storageKey('consumed')) === 'true';
  }

  private markValidationConsumed(): void {
    if (!this.hash) return;
    localStorage.setItem(this.storageKey('consumed'), 'true');
  }

  private clearWhatsappValidationData(): void {
    localStorage.removeItem('session');
    const storages = [localStorage, sessionStorage];
    for (const storage of storages) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (
          key &&
          key.startsWith('whatsapp_validation_') &&
          key.includes(this.hash) &&
          !key.startsWith(this.storageKey('consumed'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    }
  }

  private showAlreadyCompleted(): void {
    if (this.showCompleted()) {
      return;
    }
    this.showWhatsappWarning.set(false);
    this.showFaceGuide.set(false);
    this.showOlimpia.set(false);
    this.showError.set(false);
    this.wowDisplay.set(false);
    this.validationOlimpiaUrl = null;
    this.clearPollTimers();
    this.hideBecome();
    this.markValidationConsumed();
    this.showCompleted.set(true);
    this.loaderService.hide();
    this.startCompletedCountdown();
  }

  private startCompletedCountdown(): void {
    this.countdown.set(5);
    this.clearCountdown();
    this.countdownId = setInterval(() => {
      const nextValue = this.countdown() - 1;
      this.countdown.set(nextValue);
      if (nextValue <= 0) {
        this.clearCountdown();
        this.redirectToFincomercio();
      }
    }, 1000);
  }

  private redirectToFincomercio(): void {
    this.leaving = true;
    this.clearWhatsappValidationData();
    window.location.replace('https://www.fincomercio.com');
  }

  private redirectAway(): void {
    this.redirectToFincomercio();
  }

  private leaveValidationPage(): void {
    this.clearWhatsappValidationData();
    this.redirectAway();
  }

  private clearCountdown(): void {
    if (this.countdownId) {
      clearInterval(this.countdownId);
      this.countdownId = null;
    }
  }

  private getFlag(suffix: 'face_guide_shown' | 'verified'): boolean {
    return localStorage.getItem(this.storageKey(suffix)) === 'true';
  }

  private setFlag(suffix: 'face_guide_shown' | 'verified' | 'hash', value: string | boolean): void {
    localStorage.setItem(this.storageKey(suffix), String(value));
  }

  private clearPollTimers(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.pollStartTimeoutId) {
      clearTimeout(this.pollStartTimeoutId);
      this.pollStartTimeoutId = null;
    }
  }

  private clearTimers(): void {
    this.clearPollTimers();
    this.clearCountdown();
    if (this.fallbackTimeoutId) {
      clearTimeout(this.fallbackTimeoutId);
      this.fallbackTimeoutId = null;
    }
  }
}
